import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useToast } from '../../lib/toast';
import type { CreatorAccount } from '../../lib/types';
import { AdminTabBar } from './AdminTabBar';
import { AcDetail, DETAIL_TABS, type DetailTab } from './ActiveCreatorsTab';
import styles from './Dashboard.module.css';

function tabFromParam(raw: string | null): DetailTab {
  return (DETAIL_TABS as readonly string[]).includes(raw ?? '')
    ? (raw as DetailTab)
    : 'application';
}

export function CreatorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'creators'],
    queryFn: () => api.get<CreatorAccount[]>('/api/admin/creators'),
  });
  const creator = id ? (data ?? []).find((a) => a.id === id) ?? null : null;

  const tab = tabFromParam(params.get('tab'));
  const setTab = (t: DetailTab) => {
    const next = new URLSearchParams(params);
    if (t === 'application') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<CreatorAccount>(`/api/admin/creators/${id}`, body),
    onSuccess: (updated) => {
      qc.setQueryData<CreatorAccount[]>(['admin', 'creators'], (prev) =>
        prev?.map((a) => (a.id === updated.id ? updated : a)) ?? [],
      );
    },
  });

  return (
    <>
      <AdminTabBar />
      {creator ? (
        <AcDetail
          a={creator}
          tab={tab}
          onTab={setTab}
          onBack={() => navigate('/admin?tab=active')}
          onPatch={(body, msg) =>
            patch.mutate(body, {
              onSuccess: () => msg && toast(msg),
              onError: () => toast('Failed to save'),
            })
          }
          toast={toast}
        />
      ) : (
        <div className={styles.noResults}>
          {isPending ? 'Loading…' : 'Creator not found.'}
        </div>
      )}
    </>
  );
}
